import React from "react";
import { motion } from "framer-motion";

/**
 * FrameworkDiagrams — custom branded SVG glyphs and pipeline.
 * Aesthetic: Palantir/DeepMind/Anthropic — abstract, technical, animated.
 * Strict monochrome with single orange accent.
 * Each glyph is a unique pictogram for one of the H·E·L·I·O·S disciplines.
 */

const W = "#FAFAFA";
const G1 = "rgba(255,255,255,0.55)";
const G2 = "rgba(255,255,255,0.28)";
const G3 = "rgba(255,255,255,0.10)";
const O  = "#FF6600";

/* ============================================================
   1. HARVEST. Radial nodes converging inward (data ingestion)
   ============================================================ */
export function HarvestGlyph({ size = 96 }) {
  const radius = 38;
  const cx = 48, cy = 48;
  const dots = Array.from({ length: 8 }).map((_, i) => {
    const a = (i / 8) * Math.PI * 2;
    return { x: cx + Math.cos(a) * radius, y: cy + Math.sin(a) * radius, delay: i * 0.12 };
  });
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" aria-hidden="true">
      <circle cx={cx} cy={cy} r="6" fill={G3} stroke={W} strokeWidth="0.8" />
      {dots.map((d, i) => (
        <g key={i}>
          <line x1={d.x} y1={d.y} x2={cx} y2={cy} stroke={G2} strokeWidth="0.5" />
          <motion.circle
            cx={d.x} cy={d.y} r="2.4"
            fill={W}
            initial={{ opacity: 0.3 }}
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 2.6, repeat: Infinity, delay: d.delay, ease: "easeInOut" }}
          />
        </g>
      ))}
      <motion.circle
        cx={cx} cy={cy} r="6"
        fill="none"
        stroke={O}
        strokeWidth="0.8"
        initial={{ scale: 1, opacity: 0.8 }}
        animate={{ scale: [1, 1.8, 1], opacity: [0.8, 0, 0.8] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: "easeOut" }}
        style={{ transformOrigin: `${cx}px ${cy}px` }}
      />
    </svg>
  );
}

/* ============================================================
   2. ENCODE. Typed knowledge graph nodes + edges (ontology)
   ============================================================ */
export function EncodeGlyph({ size = 96 }) {
  const nodes = [
    { x: 20, y: 28, t: "E" }, { x: 76, y: 22, t: "R" }, { x: 76, y: 70, t: "P" },
    { x: 20, y: 70, t: "O" }, { x: 48, y: 48, t: "•" },
  ];
  const edges = [[4, 0], [4, 1], [4, 2], [4, 3], [0, 1], [1, 2], [2, 3], [3, 0]];
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" aria-hidden="true">
      {edges.map(([a, b], i) => (
        <motion.path
          key={i}
          d={`M ${nodes[a].x} ${nodes[a].y} L ${nodes[b].x} ${nodes[b].y}`}
          fill="none"
          stroke={G2} strokeWidth="0.7"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.6, delay: i * 0.1, ease: "easeOut", repeat: Infinity, repeatType: "reverse", repeatDelay: 1 }}
        />
      ))}
      {nodes.map((n, i) => (
        <g key={i}>
          <rect x={n.x - 6} y={n.y - 6} width="12" height="12" rx="2"
            fill={G3} stroke={i === 4 ? O : W} strokeWidth={i === 4 ? "1" : "0.7"} />
          <text x={n.x} y={n.y + 3} textAnchor="middle"
            fontFamily="JetBrains Mono" fontSize="7"
            fill={i === 4 ? O : W}>{n.t}</text>
        </g>
      ))}
    </svg>
  );
}

/* ============================================================
   3. LEARN. Bitemporal evidence ledger (timeline + memory probe)
   ============================================================ */
export function LearnGlyph({ size = 96 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" aria-hidden="true">
      {/* Two timelines: valid time (top), transaction time (bottom) */}
      <line x1="10" y1="30" x2="86" y2="30" stroke={G2} strokeWidth="0.6" />
      <line x1="10" y1="66" x2="86" y2="66" stroke={G2} strokeWidth="0.6" />

      {/* Event bars on valid time */}
      {[16, 28, 42, 58, 72].map((x, i) => (
        <motion.rect
          key={i}
          x={x - 1.5} y="24" width="3" height="12"
          fill={W}
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 0.5, delay: i * 0.15, repeat: Infinity, repeatType: "reverse", repeatDelay: 2 }}
          style={{ transformOrigin: `${x}px 30px` }}
        />
      ))}

      {/* Transaction events */}
      {[20, 34, 50, 64, 80].map((x, i) => (
        <circle key={i} cx={x} cy="66" r="2.4" fill={G3} stroke={W} strokeWidth="0.6" />
      ))}

      {/* As-known-at probe (sweeping vertical line) */}
      <motion.line
        x1="10" y1="22" x2="10" y2="74"
        stroke={O} strokeWidth="0.8"
        initial={{ x1: 12, x2: 12 }}
        animate={{ x1: [12, 84, 12], x2: [12, 84, 12] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Labels */}
      <text x="10" y="18" fontFamily="JetBrains Mono" fontSize="6" fill={G1}>τᵥ</text>
      <text x="10" y="82" fontFamily="JetBrains Mono" fontSize="6" fill={G1}>τₓ</text>
    </svg>
  );
}

/* ============================================================
   4. INFER. Branching counterfactual paths (causal tree)
   ============================================================ */
export function InferGlyph({ size = 96 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" aria-hidden="true">
      {/* Root */}
      <circle cx="48" cy="22" r="3" fill={O} />

      {/* Branches */}
      <motion.path
        d="M 48 22 L 24 48 L 12 74"
        fill="none" stroke={W} strokeWidth="0.8"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.4, delay: 0.0, repeat: Infinity, repeatType: "loop", repeatDelay: 1.5 }}
      />
      <motion.path
        d="M 48 22 L 24 48 L 36 74"
        fill="none" stroke={G2} strokeWidth="0.7" strokeDasharray="2 2"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.4, delay: 0.25, repeat: Infinity, repeatType: "loop", repeatDelay: 1.5 }}
      />
      <motion.path
        d="M 48 22 L 72 48 L 60 74"
        fill="none" stroke={G2} strokeWidth="0.7" strokeDasharray="2 2"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.4, delay: 0.5, repeat: Infinity, repeatType: "loop", repeatDelay: 1.5 }}
      />
      <motion.path
        d="M 48 22 L 72 48 L 84 74"
        fill="none" stroke={W} strokeWidth="0.8"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.4, delay: 0.75, repeat: Infinity, repeatType: "loop", repeatDelay: 1.5 }}
      />

      {/* Mid nodes */}
      <circle cx="24" cy="48" r="2.4" fill={G3} stroke={W} strokeWidth="0.6" />
      <circle cx="72" cy="48" r="2.4" fill={G3} stroke={W} strokeWidth="0.6" />

      {/* Leaves */}
      <circle cx="12" cy="74" r="2.2" fill={W} />
      <circle cx="36" cy="74" r="2.2" fill={G3} stroke={G1} strokeWidth="0.5" />
      <circle cx="60" cy="74" r="2.2" fill={G3} stroke={G1} strokeWidth="0.5" />
      <circle cx="84" cy="74" r="2.2" fill={W} />
    </svg>
  );
}

/* ============================================================
   5. ORCHESTRATE. Central hub broadcasting to many surfaces
   ============================================================ */
export function OrchestrateGlyph({ size = 96 }) {
  const targets = [
    { x: 18, y: 30, t: "UI" },
    { x: 78, y: 30, t: "API" },
    { x: 14, y: 66, t: "CLI" },
    { x: 48, y: 80, t: "MCP" },
    { x: 82, y: 66, t: "SDK" },
  ];
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" aria-hidden="true">
      {targets.map((t, i) => (
        <motion.line
          key={i}
          x1="48" y1="42" x2={t.x} y2={t.y}
          stroke={G2} strokeWidth="0.6"
          initial={{ opacity: 0.2 }}
          animate={{ opacity: [0.2, 0.9, 0.2] }}
          transition={{ duration: 2.4, delay: i * 0.18, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      {/* Central model */}
      <circle cx="48" cy="42" r="10" fill={G3} stroke={O} strokeWidth="0.9" />
      <text x="48" y="45" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="8" fill={W}>·</text>

      {/* Surfaces */}
      {targets.map((t, i) => (
        <g key={i}>
          <rect x={t.x - 8} y={t.y - 5} width="16" height="10" rx="2"
            fill={G3} stroke={W} strokeWidth="0.5" />
          <text x={t.x} y={t.y + 2} textAnchor="middle"
            fontFamily="JetBrains Mono" fontSize="6" fill={W}>{t.t}</text>
        </g>
      ))}
    </svg>
  );
}

/* ============================================================
   6. STEWARD. Append-only ledger with hash chain + verifier seal
   ============================================================ */
export function StewardGlyph({ size = 96 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 96 96" aria-hidden="true">
      {/* Ledger blocks */}
      {[16, 32, 48, 64, 80].map((x, i) => (
        <g key={i}>
          <rect x={x - 6} y="42" width="12" height="12" rx="1.5"
            fill="rgba(255,102,0,0.05)" stroke={O} strokeWidth={i === 4 ? "1" : "0.6"} strokeOpacity={i === 4 ? 1 : 0.5} />
          {i < 4 && (
            <motion.path
              d={`M ${x + 6} 48 L ${x + 10} 48`}
              fill="none"
              stroke={O} strokeWidth="0.6"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.4, delay: i * 0.3, repeat: Infinity, repeatType: "loop", repeatDelay: 1.5 }}
            />
          )}
        </g>
      ))}

      {/* Hash labels (top) */}
      {[16, 32, 48, 64, 80].map((x, i) => (
        <text key={i} x={x} y="36" textAnchor="middle"
          fontFamily="JetBrains Mono" fontSize="5" fill={G1}>r{i + 1}</text>
      ))}

      {/* Signature seal (Ed25519). Pulsing ring */}
      <motion.circle
        cx="80" cy="48" r="9"
        fill="none" stroke={W} strokeWidth="0.6"
        animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0, 0.6] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
        style={{ transformOrigin: "80px 48px" }}
      />

      {/* Verifier line (bottom) */}
      <line x1="10" y1="72" x2="86" y2="72" stroke={G2} strokeWidth="0.5" strokeDasharray="2 2" />
      <text x="48" y="84" textAnchor="middle"
        fontFamily="JetBrains Mono" fontSize="6" fill={G1}>verifier</text>
    </svg>
  );
}

export const DISC_GLYPHS = {
  H: HarvestGlyph,
  E: EncodeGlyph,
  L: LearnGlyph,
  I: InferGlyph,
  O: OrchestrateGlyph,
  S: StewardGlyph,
};

/* ============================================================
   FLOW PIPELINE. Animated H → E → L → I → O → S
   Used as the centrepiece visual on the framework page.
   Shows particles flowing through the six stages continuously.
   ============================================================ */
export function HeliosFlowPipeline() {
  // Stage positions on a horizontal flow
  const stages = [
    { x:  90, letter: "H", label: "Harvest" },
    { x: 232, letter: "E", label: "Encode" },
    { x: 374, letter: "L", label: "Learn" },
    { x: 516, letter: "I", label: "Infer" },
    { x: 658, letter: "O", label: "Orchestrate" },
    { x: 800, letter: "S", label: "Steward" },
  ];
  const y = 130;

  return (
    <svg viewBox="0 0 900 240" className="w-full h-auto" aria-label="HELIOS pipeline">
      <defs>
        <linearGradient id="flowGrad" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%"   stopColor="rgba(255,102,0,0)" />
          <stop offset="50%"  stopColor="rgba(255,102,0,0.8)" />
          <stop offset="100%" stopColor="rgba(255,102,0,0)" />
        </linearGradient>
        <marker id="flowArrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,0 L6,4 L0,8" fill="none" stroke={G1} strokeWidth="1" />
        </marker>
      </defs>

      {/* Spine */}
      <line x1="90" y1={y} x2="800" y2={y} stroke={G2} strokeWidth="0.8" />

      {/* Animated flow particles riding the spine */}
      {[0, 1, 2].map((i) => (
        <motion.circle
          key={i}
          r="3"
          fill={O}
          initial={{ cx: 90, opacity: 0 }}
          animate={{ cx: [90, 800], opacity: [0, 1, 1, 0] }}
          transition={{
            duration: 6,
            repeat: Infinity,
            delay: i * 2,
            ease: "linear",
            times: [0, 0.08, 0.92, 1],
          }}
          cy={y}
        />
      ))}

      {/* Stage nodes */}
      {stages.map((s, i) => (
        <g key={s.letter}>
          {/* Outer ring */}
          <circle cx={s.x} cy={y} r="34" fill="#141414" stroke={G2} strokeWidth="0.8" />
          {/* Letter */}
          <text
            x={s.x} y={y + 11}
            textAnchor="middle"
            fontFamily="Satoshi, system-ui"
            fontSize="32"
            fontWeight="500"
            fill={W}
            style={{ letterSpacing: "-0.04em" }}
          >
            {s.letter}
          </text>
          {/* Label below */}
          <text
            x={s.x} y={y + 64}
            textAnchor="middle"
            fontFamily="JetBrains Mono"
            fontSize="10"
            letterSpacing="0.18em"
            fill={G1}
          >
            {s.label.toUpperCase()}
          </text>
          {/* Stage index above */}
          <text
            x={s.x} y={y - 50}
            textAnchor="middle"
            fontFamily="JetBrains Mono"
            fontSize="9"
            letterSpacing="0.22em"
            fill={O}
          >
            {String(i + 1).padStart(2, "0")}
          </text>
        </g>
      ))}

      {/* Top arc. Outcome feedback (S → H closing the loop) */}
      <motion.path
        d="M 800 96 Q 445 30 90 96"
        fill="none"
        stroke={G2}
        strokeWidth="0.8"
        strokeDasharray="3 4"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 0.7 }}
        transition={{ duration: 2, delay: 0.4 }}
        markerEnd="url(#flowArrow)"
      />
      <text x="445" y="44" textAnchor="middle"
        fontFamily="editorial-new, Georgia"
        fontStyle="italic" fontSize="13" fill={G1}>
        outcomes feed back, the loop continues
      </text>

      {/* Source and sink labels */}
      <text x="90" y="220" textAnchor="middle"
        fontFamily="JetBrains Mono" fontSize="9"
        letterSpacing="0.22em" fill={G1}>RAW REALITY</text>
      <text x="800" y="220" textAnchor="middle"
        fontFamily="JetBrains Mono" fontSize="9"
        letterSpacing="0.22em" fill={W}>GOVERNED DECISION</text>
    </svg>
  );
}
